/**
 * Cliente da API. O access token vive só em memória (nunca localStorage —
 * um XSS não o leva para lado nenhum depois de fechar o separador); o
 * refresh token é um cookie HttpOnly que o JavaScript nem vê.
 */
let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export const setAccessToken = (t: string | null) => { accessToken = t; };
export const setOnSessionLost = (fn: () => void) => { onSessionLost = fn; };

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public body: Record<string, unknown> = {}) { super(message); }
  get issues(): { path: string; message: string }[] { return (this.body.issues as { path: string; message: string }[]) ?? []; }
}

const base = '/api';
const headers = (json: boolean): Record<string, string> => ({
  'x-requested-with': 'makila',
  ...(json ? { 'content-type': 'application/json' } : {}),
  ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
});

export async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const r = await fetch(`${base}/auth/refresh`, { method: 'POST', headers: headers(false), credentials: 'same-origin' });
      if (!r.ok) return false;
      const j = await r.json();
      accessToken = j.accessToken;
      return true;
    } catch { return false; } finally { setTimeout(() => { refreshing = null; }, 0); }
  })();
  return refreshing;
}

async function request(method: string, path: string, body?: unknown, retry = true): Promise<Response> {
  const isForm = body instanceof FormData;
  const r = await fetch(`${base}${path}`, {
    method, credentials: 'same-origin', headers: headers(!!body && !isForm),
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  if (r.status === 401 && retry && !path.startsWith('/auth/')) {
    if (await refreshSession()) return request(method, path, body, false);
    onSessionLost?.();
  }
  return r;
}

export async function api<T = Record<string, any>>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await request(method, path, body);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, j.code ?? 'http', j.message ?? `Erro ${r.status}`, j);
  return j as T;
}

/** Resposta em texto (HTML da pré-visualização). */
export async function apiText(path: string): Promise<string> {
  const r = await request('GET', path);
  if (!r.ok) throw new ApiError(r.status, 'http', `Erro ${r.status}`);
  return r.text();
}

/** Ficheiros (PDF, CSV…): abre num separador ou descarrega. */
export async function download(path: string, filename?: string, open = false): Promise<void> {
  const r = await request('GET', path);
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new ApiError(r.status, j.code ?? 'http', j.message ?? `Erro ${r.status}`, j);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  if (open) { window.open(url, '_blank', 'noopener'); }
  else {
    const name = filename ?? /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? 'ficheiro';
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const get = <T = Record<string, any>>(p: string) => api<T>('GET', p);
export const post = <T = Record<string, any>>(p: string, b?: unknown) => api<T>('POST', p, b ?? {});
export const patch = <T = Record<string, any>>(p: string, b: unknown) => api<T>('PATCH', p, b);
export const put = <T = Record<string, any>>(p: string, b: unknown) => api<T>('PUT', p, b);
export const del = <T = Record<string, any>>(p: string) => api<T>('DELETE', p);
